import cn from 'classnames';
import React, { useRef, useState } from 'react';

import Button from '@sats-group/ui-lib/react/button/button';
import Checkbox from '@sats-group/ui-lib/react/checkbox/checkbox';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input/hidden-input';
import LinkButton from '@sats-group/ui-lib/react/link-button/link-button';
import SegmentedController from '@sats-group/ui-lib/react/segmented-controller';
import Text from '@sats-group/ui-lib/react/text/text';

import Terms from 'shared/components/terms/terms';
import type { NamedFC } from 'shared/types/named-fc.types';

import SplitLayout from 'ui/components/split-layout/split-layout';
import FormInputs from 'ui/pages/register/form-inputs/form-inputs';

import { fillRegistrationForm } from 'utils/registration-form-filler';
import useOnPageShow from 'utils/use-on-page-show';

import {
  type Register as Props,
  SegmentedControllerTab,
} from './register.props';

export const registerFormId = 'register-form';

const Register: NamedFC<Props> = ({
  endpoint,
  existingOtherPayerTabLabel,
  generalTerms,
  hasNewOtherPayerInformation = false,
  hiddenInputs = [],
  isDebugMode,
  language,
  layout,
  login,
  marketingConsent,
  memberForm,
  newOtherPayerTabLabel,
  otherPayer,
  saveMemberInformationEndpoint,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isNewOtherPayer, setIsNewOtherPayer] = useState<boolean>(
    hasNewOtherPayerInformation
  );
  const [selectedTab, setSelectedTab] = useState<string>(
    hasNewOtherPayerInformation
      ? SegmentedControllerTab.NewOtherPayer
      : SegmentedControllerTab.ExistingOtherPayer
  );
  const formRef = useRef<HTMLFormElement>(null);

  const handleViewChange = (id: string) => {
    setSelectedTab(id);
  };

  useOnPageShow(setIsSubmitting);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    if (isSubmitting || !(event.target as HTMLFormElement).checkValidity()) {
      event.preventDefault();
      return;
    }
    setIsSubmitting(true);
  };

  const handleLogin = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (!login) {
      return;
    }

    try {
      if (formRef.current) {
        await fetch(saveMemberInformationEndpoint, {
          method: 'POST',
          body: new FormData(formRef.current),
        });
      }
    } finally {
      window.location.assign(login.link.href);
    }
  };

  return (
    <SplitLayout {...layout}>
      <div className="register">
        <form
          action={endpoint}
          method="POST"
          className="register__form"
          id={registerFormId}
          onSubmit={handleSubmit}
          ref={formRef}
        >
          {hiddenInputs.map(hiddenInput => (
            <HiddenInput key={hiddenInput.name} {...hiddenInput} />
          ))}
          {isDebugMode ? (
            <div className="register__populate-fields-btn">
              <Button
                text="Populate fields"
                onClick={() => fillRegistrationForm(language)}
              />
            </div>
          ) : null}
          <FormInputs {...memberForm} />
          {otherPayer && existingOtherPayerTabLabel && newOtherPayerTabLabel ? (
            <>
              <div className="register__other-payer">
                <Checkbox
                  theme={Checkbox.themes.blue}
                  onChange={e => setIsNewOtherPayer(e.currentTarget.checked)}
                  defaultChecked={hasNewOtherPayerInformation}
                  {...otherPayer.checkbox}
                  labelVariant={Checkbox.labelVariants.bold}
                />
                <div
                  className={cn('register__other-payer-expandable', {
                    'register__other-payer-expandable--open': isNewOtherPayer,
                  })}
                  inert={!isNewOtherPayer}
                >
                  <div className="register__other-payer-expandable-inner">
                    <SegmentedController
                      tabs={[
                        {
                          id: SegmentedControllerTab.ExistingOtherPayer,
                          label: existingOtherPayerTabLabel,
                          isSelected:
                            selectedTab ===
                            SegmentedControllerTab.ExistingOtherPayer,
                        },
                        {
                          id: SegmentedControllerTab.NewOtherPayer,
                          label: newOtherPayerTabLabel,
                          isSelected:
                            selectedTab ===
                            SegmentedControllerTab.NewOtherPayer,
                        },
                      ]}
                      onSelectionChange={handleViewChange}
                    />
                    {selectedTab ===
                    SegmentedControllerTab.ExistingOtherPayer ? (
                      <div className="register__tab-content">
                        {login ? (
                          <div className="register__other-payer-descriptions">
                            <Text theme={Text.themes.emphasis}>
                              {login.title}
                            </Text>
                            <Text size={Text.sizes.small}>
                              {login.description}
                            </Text>
                          </div>
                        ) : null}
                        {login ? (
                          <div className="register__other-payer-login-btn">
                            <LinkButton
                              {...login.link}
                              wide
                              data-test-login
                              onClick={handleLogin}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {selectedTab === SegmentedControllerTab.NewOtherPayer ? (
                      <div className="register__tab-content">
                        <div className="register__other-payer-descriptions">
                          <Text theme={Text.themes.emphasis}>
                            {otherPayer.label}
                          </Text>
                          <Text size={Text.sizes.small}>
                            {otherPayer.description}
                          </Text>
                        </div>
                        <FormInputs {...otherPayer.form} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : null}
          {generalTerms || marketingConsent ? (
            <div className="register__terms-wrapper">
              {generalTerms ? <Terms {...generalTerms} /> : null}
              {marketingConsent && marketingConsent.terms ? (
                <Terms {...marketingConsent} />
              ) : null}
            </div>
          ) : null}
        </form>
      </div>
    </SplitLayout>
  );
};

Register.displayName = 'Register';

export default Register;
